import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { PaperPlane } from "@/components/PaperPlane";
import { GoldButton } from "@/components/Buttons";
import { CALENDAR_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  business_name: z.string().min(2, "Please enter your business name"),
  city: z.string().min(2, "Please enter your city or town"),
  website: z.string().optional(),
  phone: z.string().min(7, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email"),
  business_type: z.string().min(1, "Please select a business type"),
  years_in_business: z.string().min(1, "Please select an option"),
  monthly_revenue: z.string().min(1, "Please select a range"),
  biggest_challenge: z.string().min(10, "Tell us a little more"),
  ran_meta_ads: z.string().min(1, "Please select an option"),
  ad_budget: z.string().min(1, "Please select a range"),
  why_now: z.string().min(10, "Tell us a little more"),
  heard_about: z.string().min(1, "Please select an option"),
});

const inputCls = "w-full bg-ink-800 border border-white/10 text-white px-4 py-3 outline-none focus:border-gold transition-colors placeholder:text-cream/30";
const labelCls = "block text-sm text-cream/80 mb-2";
const errCls = "text-red-400 text-xs mt-1";

const Field = ({ label, error, children, required }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-gold"> *</span>}</label>
    {children}
    {error && <p className={errCls}>{error.message}</p>}
  </div>
);

const steps = [
  "We review your application within 48 hours.",
  "If we're a potential fit, we'll reach out to schedule a discovery call.",
  "On the call, we'll map out exactly what working together looks like.",
];

export default function Apply() {
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/applications`, data);
      setSubmitted(true);
      toast.success("Application sent! We'll be in touch within 48 hours.");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <>
      <Seo
        title="Apply to Work With Prolific Maven | Meta Ads Agency | Florida"
        description="Apply to work with Prolific Maven. We partner with a limited number of local Florida businesses at a time. Tell us about yours and we'll see if we're a fit."
        path="/apply"
      />
      <PageHero
        eyebrow="Apply"
        title="Let's find out if we're a fit."
        subtitle="We work with a limited number of local businesses at a time. Tell us about yours."
      />

      <section className="relative z-10 bg-ink-800 border-t border-white/10 py-16 sm:py-24" data-testid="apply-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {submitted ? (
              <div className="bg-ink-900 border border-gold/30 p-10 sm:p-16 text-center" data-testid="apply-success">
                <PaperPlane className="w-12 h-12 mx-auto mb-6" />
                <h2 className="font-display font-black text-3xl sm:text-4xl text-white">Your application is on its way.</h2>
                <p className="mt-4 text-cream/70 max-w-xl mx-auto">Thank you for reaching out. We review every application personally and will be in touch within 48 hours. Want to move faster? Book your discovery call now.</p>
                <div className="mt-8 flex justify-center">
                  <GoldButton href={CALENDAR_URL} testid="apply-book-call">Book a Discovery Call →</GoldButton>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="apply-form" noValidate>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Full Name" error={errors.full_name} required>
                    <input data-testid="input-full_name" className={inputCls} {...register("full_name")} />
                  </Field>
                  <Field label="Business Name" error={errors.business_name} required>
                    <input data-testid="input-business_name" className={inputCls} {...register("business_name")} />
                  </Field>
                  <Field label="City / Town (in Florida)" error={errors.city} required>
                    <input data-testid="input-city" className={inputCls} {...register("city")} />
                  </Field>
                  <Field label="Website (optional)" error={errors.website}>
                    <input data-testid="input-website" className={inputCls} placeholder="https://" {...register("website")} />
                  </Field>
                  <Field label="Phone Number" error={errors.phone} required>
                    <input data-testid="input-phone" className={inputCls} {...register("phone")} />
                  </Field>
                  <Field label="Email Address" error={errors.email} required>
                    <input data-testid="input-email" type="email" className={inputCls} {...register("email")} />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="What type of business do you run?" error={errors.business_type} required>
                    <select data-testid="input-business_type" className={inputCls} defaultValue="" {...register("business_type")}>
                      <option value="" disabled>Select…</option>
                      {["Home Services", "Health & Wellness", "Professional Services", "Retail / E-commerce", "Real Estate", "Hospitality / Food", "Trades / Construction", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="How long have you been in business?" error={errors.years_in_business} required>
                    <select data-testid="input-years_in_business" className={inputCls} defaultValue="" {...register("years_in_business")}>
                      <option value="" disabled>Select…</option>
                      {["Less than 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Approximate monthly revenue?" error={errors.monthly_revenue} required>
                    <select data-testid="input-monthly_revenue" className={inputCls} defaultValue="" {...register("monthly_revenue")}>
                      <option value="" disabled>Select…</option>
                      {["Under $10k", "$10k-$25k", "$25k-$50k", "$50k-$100k", "$100k+"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Have you run Meta ads before?" error={errors.ran_meta_ads} required>
                    <select data-testid="input-ran_meta_ads" className={inputCls} defaultValue="" {...register("ran_meta_ads")}>
                      <option value="" disabled>Select…</option>
                      {["Yes", "No", "Not sure"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Monthly ad budget or target?" error={errors.ad_budget} required>
                    <select data-testid="input-ad_budget" className={inputCls} defaultValue="" {...register("ad_budget")}>
                      <option value="" disabled>Select…</option>
                      {["Under $1k/mo", "$1k-$3k/mo", "$3k-$5k/mo", "$5k-$10k/mo", "$10k+/mo", "Not sure yet"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="How did you hear about us?" error={errors.heard_about} required>
                    <select data-testid="input-heard_about" className={inputCls} defaultValue="" {...register("heard_about")}>
                      <option value="" disabled>Select…</option>
                      {["Referral", "Facebook / Instagram", "Google Search", "LinkedIn", "Local event", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="What's your biggest marketing challenge right now?" error={errors.biggest_challenge} required>
                  <textarea data-testid="input-biggest_challenge" rows={4} className={inputCls} {...register("biggest_challenge")} />
                </Field>
                <Field label="Why are you reaching out now?" error={errors.why_now} required>
                  <textarea data-testid="input-why_now" rows={4} className={inputCls} {...register("why_now")} />
                </Field>

                <button type="submit" disabled={isSubmitting} data-testid="submit-application" className="inline-flex items-center gap-2 bg-gold text-ink-900 font-bold px-10 py-4 hover:bg-gold-light transition-colors disabled:opacity-60 shadow-[0_0_26px_rgba(201,168,76,0.3)]">
                  {isSubmitting ? "Sending…" : "Send My Application"} <span aria-hidden>→</span>
                </button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-ink-900 border border-white/10 p-8 lg:sticky lg:top-28">
              <h3 className="font-display font-bold text-xl text-white">What happens next</h3>
              <ol className="mt-6 space-y-6">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-display font-black text-gold text-2xl leading-none">0{i + 1}</span>
                    <p className="text-cream/70 text-sm leading-relaxed">{s}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-cream/50 text-sm">Prefer to talk first?</p>
                <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer" className="text-gold text-sm link-underline" data-testid="apply-aside-calendar">Book a discovery call →</a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
